import { CurrencyPipe, NgClass, CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { PaymentCollectFormType } from '../../../pages/collect/collect.component';
import { CardExpirationPipe } from '../../../../../shared/pipes/card-expiration.pipe';
import { CcMaskPipe } from '../../../../../shared/pipes/cc-mask.pipe';
import {
  PaymentMethodMode,
  PaymentMethodModel,
  AchMethodModel,
  OneTimePaymentMethodModel,
} from 'features/customer/customer.model';
import { ActivatedRoute } from '@angular/router';
import { TransactionsService } from 'features/transactions/services/transactions.service';
import { InvoiceCheckoutItem, MultiInvoiceCheckoutData } from '../../../payment.model';
import { UserStore } from 'app/features/user/store/user.store';

@Component({
  selector: 'app-summary',
  standalone: true,
  imports: [MatButtonModule, MatIcon, NgClass, CardExpirationPipe, CcMaskPipe, CurrencyPipe, CommonModule],
  templateUrl: './summary.component.html',
  host: {
    class: 'app-summary',
  },
})
export class SummaryComponent implements OnInit {
  @Output() editClick = new EventEmitter<string>();
  @Input() form: PaymentCollectFormType | null = null;
  transactionDetails: any = null;

  private userStore = inject(UserStore);

  constructor(public route: ActivatedRoute, private transactionsService: TransactionsService) {}

  get hideTax(): boolean {
    return this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableTax === false;
  }

  ngOnInit() {
    const view = this.route.snapshot.queryParams['view'];
    const transactionId = this.route.snapshot.queryParams['transactionId'];

    if ((view === 'refund' || view === 'completeAuth') && transactionId) {
      this.transactionsService.getTransaction(transactionId).subscribe({
        next: (details) => {
          this.transactionDetails = details;
        },
        error: (error) => {
          console.error('Error fetching transaction details:', error);
        },
      });
    }
    if (view === 'adjust' && transactionId) {
      this.transactionsService.getTransaction(transactionId).subscribe({
        next: (details) => {
          this.transactionDetails = details;
        },
        error: (error) => {
          console.error('Error fetching transaction details:', error);
        },
      });
    }
  }

  get paymentMethod(): PaymentMethodModel {
    return this.form?.getRawValue().payment?.paymentMethod ?? {};
  }
  get paymentMode(): PaymentMethodMode {
    return this.form?.getRawValue().payment?.paymentMode ?? PaymentMethodMode.creditcard;
  }

  get isFlashPay(): boolean {
    const formData = this.form?.getRawValue();
    const paymentMode = formData?.payment?.paymentMode;
    if (paymentMode == PaymentMethodMode.flashpay) {
      return true;
    }
    return false;
  }

  onEditClick(value: string) {
    this.editClick.emit(value);
  }
  getCardExpirationOptions() {
    return {
      fromFormat: /^(?<month>.{2})(?<year>.{2})$/,
      toFormat: 'MM/yy',
    };
  }

  sanitizeCurrency(value: number) {
    if (isNaN(value)) {
      return 0;
    }
    return value;
  }

  protected readonly PaymentMethodMode = PaymentMethodMode;

  get isRefundView(): boolean {
    return this.route.snapshot.queryParams['view'] === 'refund';
  }

  get isAdjustView(): boolean {
    return this.route.snapshot.queryParams['view'] === 'adjust';
  }

  get isCompleteAuthView(): boolean {
    return this.route.snapshot.queryParams['view'] === 'completeAuth';
  }

  isCardPayment(paymentMethod: PaymentMethodModel): paymentMethod is PaymentMethodModel {
    return 'expirationDate' in paymentMethod;
  }

  isAchPayment(paymentMethod: PaymentMethodModel): paymentMethod is AchMethodModel {
    return (paymentMethod as any).tokenType === 'ACH';
  }

  isOneTimePayment(paymentMethod: PaymentMethodModel): paymentMethod is OneTimePaymentMethodModel {
    return 'customerName' in paymentMethod;
  }

  getCardAccount(paymentMethod: PaymentMethodModel): string {
    return this.isCardPayment(paymentMethod) ? paymentMethod.account || '' : '';
  }

  getCardExpiration(paymentMethod: PaymentMethodModel): string {
    return this.isCardPayment(paymentMethod) ? paymentMethod.expirationDate || '' : '';
  }

  getViewText(type: 'title' | 'label'): string {
    const view = this.route.snapshot.queryParams['view'];

    if (view === 'preAuth') {
      return type === 'title' ? 'Requested Amount' : 'Pre-Authorized Amount:';
    } else if (view === 'completeAuth') {
      return type === 'title' ? 'Authorization Details' : 'Authorization Amount:';
    } else if (view === 'refund') {
      return type === 'title' ? 'Refund Details' : 'Refund Amount:';
    } else if (view === 'adjust') {
      return type === 'title' ? 'Adjust Details' : 'Adjust Amount:';
    } else {
      return type === 'title' ? 'Invoice Details' : 'Invoice Total:';
    }
  }

  // Multi-invoice mode getters
  get isMultiInvoiceMode(): boolean {
    const formValue = this.form?.getRawValue();
    const invoiceValue = formValue?.invoice;
    const isMulti = invoiceValue?.isMultiInvoiceMode ?? false;
    return isMulti;
  }

  get multiInvoiceData(): MultiInvoiceCheckoutData | null {
    const data = this.form?.getRawValue().invoice?.multiInvoiceData ?? null;
    return data;
  }

  getTotalAmount(): number {
    if (this.isMultiInvoiceMode && this.multiInvoiceData) {
      return this.multiInvoiceData.totalAmount;
    }
    // Existing single invoice total calculation
    const totalAmount = this.form?.getRawValue().invoice?.totalAmount;
    return parseFloat(String(totalAmount ?? '0'));
  }

  /** Cash charged before capital cost: invoice cash plus anything applied to the account. */
  getInvoiceSubtotal(): number {
    const data = this.multiInvoiceData;
    if (!data) {
      return 0;
    }
    if (data.cashSubtotal !== undefined) {
      return data.cashSubtotal;
    }
    return (data.invoices ?? []).reduce((sum, item) => sum + this.cashOf(item), 0) + (data.onAccountAmount ?? 0);
  }

  /** What the charge puts on an invoice — its payment less any credit applied to it. */
  cashOf(item: InvoiceCheckoutItem): number {
    return item.cashAmount ?? item.paymentAmount;
  }

  hasCredits(): boolean {
    return (this.multiInvoiceData?.creditsAppliedAmount ?? 0) > 0;
  }

  /** Columns in the multi-invoice table, so the footer rows span it whatever shows. */
  summaryColumns(): number {
    return this.hasCredits() ? 5 : 4;
  }

  hasCapitalCost(): boolean {
    return this.multiInvoiceData?.capitalCostEnabled === true && (this.multiInvoiceData?.capitalCostAmount ?? 0) > 0;
  }
}
